// src/pages/contacts/ContactsScreen.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdPhone,
  MdDownload,
  MdDescription,
  MdExpandMore,
  MdExpandLess,
  MdAccessTime,
} from "react-icons/md";
import { useTheme } from "../../common/hooks/useTheme";
import { ProductHeader } from "../../common/components/productHeader/ProductHeader";
import styles from "./ContactsScreen.module.scss";
import { ROUTES } from "../../common/utils/constants";
import { useSelector } from "react-redux";
import { selectStoreList } from "../../store/slices/storeListSlice";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import StoreSchedule from "../../common/components/storeSchedule/StoreSchedule";
import { MediaCard } from "../../common/components/mediaCard/MediaCard";
import { PageMeta } from "../../common/components/pageMeta/PageMeta";
import { GoalType, EntityType } from "../../common/types/enums";
import useMetrics from "../../common/hooks/useMetrics";
import { Documents } from "../../common/components/documents/Documents";

export default function ContactsScreen() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const list = useSelector(selectStoreList);
  const activeStores = list.filter((store) => store.isActive);
  const cluster = useSelector(selectCurrentCluster);
  const { trackGoal } = useMetrics();
  const isQsoil = cluster?.id === 7;

  const [docsExpanded, setDocsExpanded] = useState(false);
  const [scheduleExpanded, setScheduleExpanded] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);

  const ruStoreUrl = process.env.REACT_APP_RUSTORE_URL;
  const appStoreUrl = process.env.REACT_APP_APPSTORE_URL;
  const directDownloadUrl = process.env.REACT_APP_DIRECT_DOWNLOAD_URL;

  const isPrerender = window.location.search.includes("__prerender=1");

  useEffect(() => {
    if (isPrerender) {
      setDocsExpanded(true);
      setScheduleExpanded(true);
      setAboutExpanded(true);
    }
  }, [isPrerender]);

  const handleContactPress = (contact: any) => {
    navigate(`${ROUTES.CONTACTS_ITEM}/${contact.id}`, {
      state: { item: contact },
    });
  };

  const handlePhonePress = (phone: string) => {
    trackGoal(GoalType.CALL_CLICK, phone);
    window.location.href = `tel:${phone}`;
  };

  const handleLinkPress = (url: string) => {
    window.open(url, "_blank");
  };

  const getBadgeText = (item: any) => {
    if (!item.temporarilyDisabled) return undefined;
    return "Временно не работает";
  };

  return (
    <div
      className={styles.container}
      style={{ backgroundColor: theme.background }}
    >
      <PageMeta />
      <ProductHeader
        title="Наши контакты"
        isFavorite={false}
        showFavorite={false}
        textColor={theme.text}
        borderColor={theme.border}
      />

      <p className={styles.subtitle} style={{ color: theme.textSecondary }}>
        {isQsoil
          ? "Выберите склад, чтобы посмотреть адрес, контакты и режим отгрузки"
          : "Выберите торговую точку, для подробностей"}
      </p>

      <div className={styles.content}>
        {activeStores.length > 0 && (
          <div className={styles.list}>
            {activeStores.map((item) => (
              <MediaCard
                key={item.id}
                imageUrl={item.files[0]}
                title={item.name}
                description={item.description}
                to={`${ROUTES.CONTACTS_ITEM}/${item.id}`}
                badge={getBadgeText(item)}
              />
            ))}
          </div>
        )}
      </div>

      <div
        className={styles.bottomBlock}
        style={{
          backgroundColor: theme.card,
          borderColor: theme.border,
        }}
      >
        <div className={styles.section}>
          <button
            className={styles.expandableHeader}
            onClick={() => setDocsExpanded(!docsExpanded)}
            style={{ color: theme.onCard }}
          >
            <span>Документы</span>
            {docsExpanded ? (
              <MdExpandLess size={20} />
            ) : (
              <MdExpandMore size={20} />
            )}
          </button>

          {docsExpanded && (
            <div className={styles.expandableContent}>
              <button
                className={styles.docLink}
                onClick={() => navigate(ROUTES.PERSONAL_DATA_AGREEMENT)}
                style={{ color: theme.primary }}
              >
                <MdDescription size={16} />
                <span>Согласие на обработку данных</span>
              </button>
              <button
                className={styles.docLink}
                onClick={() => navigate(ROUTES.USER_AGREEMENT)}
                style={{ color: theme.primary }}
              >
                <MdDescription size={16} />
                <span>Пользовательское соглашение</span>
              </button>
              <button
                className={styles.docLink}
                onClick={() => navigate(ROUTES.PRIVACY_POLICY)}
                style={{ color: theme.primary }}
              >
                <MdDescription size={16} />
                <span>Политика конфиденциальности</span>
              </button>

              {cluster?.id && (
                <Documents
                  entityType={EntityType.CLUSTER}
                  entityId={Number(cluster.id)}
                  noTitle
                  embedded
                />
              )}
            </div>
          )}
        </div>

        {cluster?.schedule && cluster.withDelivery && (
          <div className={styles.section}>
            <button
              className={styles.expandableHeader}
              onClick={() => setScheduleExpanded(!scheduleExpanded)}
              style={{ color: theme.onCard }}
            >
              <div className={styles.headerLeft}>
                <MdAccessTime size={18} />
                <span>Режим работы доставки</span>
              </div>
              {scheduleExpanded ? (
                <MdExpandLess size={20} />
              ) : (
                <MdExpandMore size={20} />
              )}
            </button>

            {scheduleExpanded && (
              <div className={styles.expandableContent}>
                <StoreSchedule schedule={cluster.schedule} />
              </div>
            )}
          </div>
        )}

        <div className={styles.section}>
          <button
            className={styles.expandableHeader}
            onClick={() => setAboutExpanded(!aboutExpanded)}
            style={{ color: theme.onCard }}
          >
            <span>О нас</span>
            {aboutExpanded ? (
              <MdExpandLess size={20} />
            ) : (
              <MdExpandMore size={20} />
            )}
          </button>

          {aboutExpanded && (
            <div className={styles.expandableContent}>
              <p className={styles.infoText} style={{ color: theme.onCard }}>
                {cluster?.description}
              </p>
            </div>
          )}
        </div>

        <h3 className={styles.infoTitle} style={{ color: theme.onCard }}>
          Телефон
        </h3>
        <button
          className={styles.phoneLink}
          onClick={() => handlePhonePress(cluster?.phone_number || "")}
          style={{ color: theme.primary }}
        >
          <MdPhone size={16} />
          <span>{cluster?.phone_number}</span>
        </button>

        {(ruStoreUrl || directDownloadUrl) && (
          <>
            <h3 className={styles.infoTitle} style={{ color: theme.onCard }}>
              Скачать приложение
            </h3>
            <div className={styles.downloadLinks}>
              {appStoreUrl && (
                <button
                  className={styles.docLink}
                  onClick={() => handleLinkPress(appStoreUrl)}
                  style={{ color: theme.primary }}
                >
                  <MdDownload size={16} />
                  <span>App Store</span>
                </button>
              )}
              {ruStoreUrl && (
                <button
                  className={styles.docLink}
                  onClick={() => handleLinkPress(ruStoreUrl)}
                  style={{ color: theme.primary }}
                >
                  <MdDownload size={16} />
                  <span>RuStore</span>
                </button>
              )}
              {directDownloadUrl && (
                <button
                  className={styles.docLink}
                  onClick={() => handleLinkPress(directDownloadUrl)}
                  style={{ color: theme.primary }}
                >
                  <MdDownload size={16} />
                  <span>Прямая ссылка для Android и Huawei</span>
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
